import { useEffect, useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarDays } from "lucide-react";

const TIMES = Array.from({ length: 24 }, (_, h) => [`${String(h).padStart(2, "0")}:00`, `${String(h).padStart(2, "0")}:30`]).flat();

const pad = (n) => String(n).padStart(2, "0");
const toIsoLocal = (d, hhmm) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${hhmm || "10:00"}`;
const parseIso = (s) => (s ? new Date(s) : null);
const timeOf = (s) => (s && s.includes("T") ? s.split("T")[1].slice(0, 5) : "10:00");

/**
 * DateTimePicker — calendar + time select. Accepts `blocked` list of {start, end}
 * ISO datetime strings and disables any day that falls inside a blocked range.
 */
export default function DateTimePicker({ value, onChange, blocked = [], minDate, label, testId }) {
  const [open, setOpen] = useState(false);
  const selected = parseIso(value);
  const time = timeOf(value);

  const isBlocked = (day) => {
    // Compare by whole days so any day inside a blocked range disables the cell
    const dayStart = new Date(day); dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(day); dayEnd.setHours(23, 59, 59, 999);
    for (const b of blocked) {
      const s = new Date(b.start); const e = new Date(b.end);
      if (dayStart <= e && s <= dayEnd) return true;
    }
    return false;
  };

  const disabled = (day) => {
    if (minDate && day < new Date(new Date(minDate).setHours(0, 0, 0, 0))) return true;
    if (day < new Date(new Date().setHours(0, 0, 0, 0))) return true;
    return isBlocked(day);
  };

  const onDate = (d) => {
    if (!d) return;
    onChange(toIsoLocal(d, time));
  };
  const onTime = (t) => {
    if (!selected) return;
    onChange(toIsoLocal(selected, t));
  };

  return (
    <div>
      {label && <label className="text-xs text-[#A3A3A3] mb-1.5 block">{label}</label>}
      <div className="flex gap-2">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid={testId}
              className="flex-1 piki-input text-sm text-left flex items-center gap-2 hover:border-[#525252]"
            >
              <CalendarDays className="w-4 h-4 text-[#D9A62E] shrink-0" />
              <span className={selected ? "text-white" : "text-[#737373]"}>
                {selected ? selected.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" }) : "Choose date"}
              </span>
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-auto p-0 bg-[#171717] border-[#2E2E2E] text-white">
            <Calendar
              mode="single"
              selected={selected}
              onSelect={(d) => { onDate(d); setOpen(false); }}
              disabled={disabled}
              initialFocus
              className="[&_.rdp-day_disabled]:line-through [&_.rdp-day_disabled]:text-[#525252] [&_.rdp-day_disabled]:bg-[#EF4444]/5"
              classNames={{
                day_selected: "bg-[#D9A62E] text-[#050505] hover:bg-[#D9A62E] hover:text-[#050505] focus:bg-[#D9A62E]",
                day_today: "bg-[#2E2E2E] text-[#D9A62E]",
                head_cell: "text-[#737373] rounded-md w-8 font-normal text-[0.75rem] uppercase tracking-wider",
                caption_label: "text-sm font-semibold text-white",
                nav_button: "h-7 w-7 bg-transparent p-0 opacity-70 hover:opacity-100 hover:bg-[#2E2E2E] rounded-md flex items-center justify-center text-white",
                day: "h-8 w-8 p-0 font-normal aria-selected:opacity-100 hover:bg-[#2E2E2E] hover:text-[#D9A62E] rounded-md text-white",
              }}
            />
            <div className="px-3 pb-3 text-[10px] text-[#737373] flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-sm bg-[#EF4444]/40" /> Unavailable dates are struck through
            </div>
          </PopoverContent>
        </Popover>
        <select
          value={time}
          onChange={(e) => onTime(e.target.value)}
          disabled={!selected}
          data-testid={testId ? `${testId}-time` : undefined}
          className="piki-input text-sm w-24 disabled:opacity-40"
        >
          {TIMES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>
    </div>
  );
}

export function useAvailability(vehicleId) {
  const [blocked, setBlocked] = useState([]);
  useEffect(() => {
    if (!vehicleId) { setBlocked([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const { api } = await import("@/lib/api");
        const r = await api.get(`/vehicles/${vehicleId}/availability`);
        if (!cancelled) setBlocked(r.data.blocked || []);
      } catch { if (!cancelled) setBlocked([]); }
    })();
    return () => { cancelled = true; };
  }, [vehicleId]);
  return blocked;
}
